import { Controller } from "@hotwired/stimulus"
import Tagify from "@yaireo/tagify"

// Connects to data-controller="tagify"
export default class extends Controller {
  static values = {
    url: String,           // URL to fetch tag suggestions from
    suggestions: Array,    // Optional: featured/suggested tags to show first
    whitelist: Array,      // Optional: predefined list of allowed tags
    enforceWhitelist: Boolean, // Whether to enforce whitelist (default: false)
    maxTags: Number,       // Maximum number of tags allowed
    placeholder: String    // Placeholder text
  }

  connect() {
    this.initializeTagify()
  }

  initializeTagify() {
    // Default settings
    const settings = {
      dropdown: {
        enabled: 0,           // Show dropdown immediately
        maxItems: 20,         // Max suggestions to show
        classname: "tagify__dropdown",
        closeOnSelect: false  // Keep dropdown open after selection
      },
      duplicates: false,
      trim: true,
      placeholder: this.placeholderValue || "Add tags...",
      originalInputValueFormat: valuesArr => valuesArr.map(item => item.value).join(', ')
    }

    // Add max tags if specified
    if (this.hasMaxTagsValue) {
      settings.maxTags = this.maxTagsValue
    }

    // Handle suggestions (featured tags shown first)
    if (this.hasSuggestionsValue && this.suggestionsValue.length > 0) {
      // Convert array of strings to Tagify format [{value: "tag"}]
      settings.whitelist = this.suggestionsValue.map(tag => ({ value: tag }))
    }

    // Handle whitelist and enforcement
    if (this.hasWhitelistValue && this.whitelistValue.length > 0) {
      settings.whitelist = this.whitelistValue
      settings.enforceWhitelist = this.enforceWhitelistValue || false
    } else if (this.hasUrlValue) {
      // Fetch whitelist from URL (merges with suggestions if provided)
      if (!this.hasSuggestionsValue) {
        settings.whitelist = []
      }
      this.fetchWhitelist()
    }

    // Initialize Tagify
    this.tagify = new Tagify(this.element, settings)

    // If URL is provided, set up autocomplete
    if (this.hasUrlValue) {
      this.tagify.on("input", this.onInput.bind(this))
    }
  }

  disconnect() {
    if (this.tagify) {
      this.tagify.destroy()
    }
  }

  async fetchWhitelist() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        const data = await response.json()

        // Merge suggestions with fetched data (suggestions first, then unique fetched tags)
        if (this.hasSuggestionsValue && this.suggestionsValue.length > 0) {
          const suggestions = this.suggestionsValue.map(tag => ({ value: tag }))
          const fetchedTags = data.filter(tag =>
            !this.suggestionsValue.includes(tag.value)
          )
          this.tagify.whitelist = [...suggestions, ...fetchedTags]
        } else {
          this.tagify.whitelist = data
        }
      }
    } catch (error) {
      console.error("Failed to fetch tags:", error)
    }
  }

  onInput(e) {
    const value = e.detail.value

    // Filter whitelist based on input
    this.tagify.whitelist = null // reset the whitelist

    // Show loading state
    this.tagify.loading(true).dropdown.hide()

    fetch(this.urlValue, {
      headers: {
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        // Filter results based on input
        let filtered = data.filter(tag =>
          tag.value.toLowerCase().includes(value.toLowerCase())
        )

        // If suggestions exist, merge them in first (filtered by input too)
        if (this.hasSuggestionsValue && this.suggestionsValue.length > 0) {
          const filteredSuggestions = this.suggestionsValue
            .filter(tag => tag.toLowerCase().includes(value.toLowerCase()))
            .map(tag => ({ value: tag }))

          // Remove suggestions from fetched data to avoid duplicates
          filtered = filtered.filter(tag =>
            !this.suggestionsValue.includes(tag.value)
          )

          filtered = [...filteredSuggestions, ...filtered]
        }

        this.tagify.whitelist = filtered
        this.tagify.loading(false).dropdown.show(value)
      })
      .catch(error => {
        console.error("Failed to fetch tags:", error)
        this.tagify.loading(false)
      })
  }
}
